import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import Breadcrumb from '../../../core/common/Breadcrumb/breadcrumb'
import ImageWithBasePath from '../../../core/common/imageWithBasePath'
import StudentSidebar from '../common/studentSidebar'
import ProfileCard from '../common/profileCard'
import { all_routes } from '../../router/all_routes'
import {
  calculateDiscount,
  rupiahFormatter,
  downloadCertificate,
  getStoredCertificateName,
  prepareCertificateDownload,
  setStoredCertificateName,
  CertificateMeta,
} from '../../../utils/helpers'
import { getCBTDetail } from '../../../api/courses'
import {
  getPurchasedCourseDetail,
  PurchasedCourseDetailResponse,
} from '../../../api/user'

function formatDateWithTimeZone(dateString: string) {
  if (!dateString) return ''
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('id-ID', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Jakarta',
    timeZoneName: 'short',
  })
    .format(date)
    .replace('GMT+7', 'WIB')
}

const LiveEventDetails = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [showCertificateModal, setShowCertificateModal] = useState(false)
  const [certificateName, setCertificateName] = useState('')
  const [storedCertificateName, setStoredCertificateNameState] = useState('')
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  })
  const [attemptInfo, setAttemptInfo] = useState({
    maxAttempt: 0,
    usedAttempt: 0,
  })

  // Fetch course data using the new API
  const { data: courseResponse, isLoading, isError, error } =
    useQuery<PurchasedCourseDetailResponse>({
    queryKey: ['purchasedCourseDetail', id],
    queryFn: () => getPurchasedCourseDetail(id!),
    enabled: !!id,
  })

  const course = courseResponse?.data
  const savedCertificateName =
    course?.certificate_downloads?.name || storedCertificateName

  // Countdown effect for live teaching
  useEffect(() => {
    if (!course?.start_date) return

    const timer = setInterval(() => {
      const now = new Date().getTime()
      const startDate = new Date(course.start_date).getTime()
      const timeLeft = startDate - now

      if (timeLeft > 0) {
        const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24))
        const hours = Math.floor(
          (timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
        )
        const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60))
        const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000)

        setCountdown({ days, hours, minutes, seconds })
      } else {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 })
      }
    }, 1000)

    return () => clearInterval(timer)
  }, [course?.start_date])

  // Determine event status
  const getEventStatus = () => {
    if (!course?.start_date || !course?.end_date) return 'upcoming'

    const now = new Date().getTime()
    const startDate = new Date(course.start_date).getTime()
    const endDate = new Date(course.end_date).getTime()

    if (now > endDate) {
      return 'ended'
    }
    if (now >= startDate && now <= endDate) {
      return 'live'
    }
    return 'upcoming'
  }

  const eventStatus = getEventStatus()

  useEffect(() => {
    if (!savedCertificateName || !course?.id) return
    if (
      course.certificate_downloads?.certificate_number ||
      course.certificate_downloads?.qr_url
    ) {
      return
    }

    prepareCertificateDownload(Number(course.id), savedCertificateName)
  }, [
    course?.id,
    course?.certificate_downloads?.name,
    course?.certificate_downloads?.certificate_number,
    course?.certificate_downloads?.qr_url,
    savedCertificateName,
  ])

  useEffect(() => {
    setStoredCertificateNameState(getStoredCertificateName(course?.id))
  }, [course?.id])

  const handleJoinGroup = () => {
    if (course?.link_group) {
      window.open(course.link_group, '_blank')
    }
  }

  const handleJoinKelas = () => {
    // For live events, you might want to use a different link or generate one
    // For now, using the group link as placeholder
    if (course?.link_group) {
      window.open(course.link_group, '_blank')
    }
  }

  const handleAksesRekaman = () => {
    // Access the recording link from the API response
    if (course?.link_record) {
      window.open(course.link_record, '_blank')
    } else {
      alert('Link rekaman belum tersedia')
    }
  }

  const handleDownloadSertifikat = async () => {
    const materialTopicOnly =
      Number(course?.questions_count || course?.questions?.length || 0) <= 0
    const certificateMeta: CertificateMeta = {
      eventDates: course?.certificate_event_dates || undefined,
      jplTotal: course?.certificate_jpl_total || undefined,
      jplNote: course?.certificate_jpl_note || undefined,
      speakers: course?.certificate_speakers || undefined,
      materials: course?.certificate_materials || undefined,
      materialTopicOnly,
      location: course?.certificate_location || undefined,
      signatureDate: course?.end_date || undefined,
      certificateNumber:
        course?.certificate_downloads?.certificate_number || undefined,
      qrUrl: course?.certificate_downloads?.qr_url || undefined,
    }

    // Check if certificate_downloads exists and has a name
    if (savedCertificateName) {
      // Direct download with existing name
      const courseTopics =
        course?.studied?.map((topic) => ({ name: topic })) || []

      try {
        await downloadCertificate(
          course?.title || 'Live Teaching Certificate',
          undefined, // customFileName
          undefined, // pdfUrl
          savedCertificateName, // userName - use existing name
          course?.title, // courseTitle
          true, // useAutoGeneration
          course?.id, // courseId
          courseTopics, // courseTopics
          false,
          '',
          '',
          certificateMeta,
        )
      } catch (error) {
        console.error('Error downloading certificate:', error)
        alert('Gagal mengunduh sertifikat. Silakan coba lagi.')
      }
    } else {
      // Show modal for first time download (certificate_downloads is null)
      handleOpenModal()
    }
  }

  const handleOpenModal = () => {
    // Reset certificate name when opening modal
    setCertificateName('')
    setShowCertificateModal(true)
  }

  const handleConfirmDownload = async () => {
    if (!certificateName.trim()) {
      alert('Nama lengkap harus diisi')
      return
    }

    const courseTopics =
      course?.studied?.map((topic) => ({ name: topic })) || []
    const materialTopicOnly =
      Number(course?.questions_count || course?.questions?.length || 0) <= 0
    const certificateMeta: CertificateMeta = {
      eventDates: course?.certificate_event_dates || undefined,
      jplTotal: course?.certificate_jpl_total || undefined,
      jplNote: course?.certificate_jpl_note || undefined,
      speakers: course?.certificate_speakers || undefined,
      materials: course?.certificate_materials || undefined,
      materialTopicOnly,
      location: course?.certificate_location || undefined,
      signatureDate: course?.end_date || undefined,
      certificateNumber:
        course?.certificate_downloads?.certificate_number || undefined,
      qrUrl: course?.certificate_downloads?.qr_url || undefined,
    }

    try {
      setStoredCertificateName(course?.id, certificateName)
      setStoredCertificateNameState(certificateName)

      // Download certificate with user's name
      await downloadCertificate(
        course?.title || 'Live Teaching Certificate',
        undefined, // customFileName
        undefined, // pdfUrl
        certificateName, // userName - use input name
        course?.title, // courseTitle
        true, // useAutoGeneration
        course?.id, // courseId
        courseTopics, // courseTopics
        false,
        '',
        '',
        certificateMeta,
      )

      setShowCertificateModal(false)
    } catch (error) {
      console.error('Error downloading certificate:', error)
      alert('Gagal mengunduh sertifikat. Silakan coba lagi.')
    }
  }

  const handleDownloadMateri = () => {
    if (course?.link_ebook) {
      window.open(course.link_ebook, '_blank')
    }
  }

  const handleStartQuiz = () => {
    if (course?.id) {
      navigate(all_routes.studentCBTInstruction.replace(':id', String(course.id)))
    }
  }

  useEffect(() => {
    const fetchAttemptInfo = async () => {
      if (!course?.id) return
      const questionCount = Number(course?.questions_count || course?.questions?.length || 0)
      if (questionCount <= 0) return

      try {
        const response = await getCBTDetail(String(course.id))
        const maxAttempt = Number(response.data.max_attempt || 0)
        const usedAttempt = Number(response.data.user_attempt_count || 0)
        setAttemptInfo({ maxAttempt, usedAttempt })
      } catch {
        setAttemptInfo({ maxAttempt: 0, usedAttempt: 0 })
      }
    }

    fetchAttemptInfo()
  }, [course?.id, course?.questions_count, course?.questions])

  const remainingAttempt = Math.max(
    0,
    attemptInfo.maxAttempt - attemptInfo.usedAttempt,
  )

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading live event details...</p>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="text-center py-5 text-danger">
        <p>Error loading live event: {error?.message || 'Unknown error'}</p>
        <p>Please try again later.</p>
      </div>
    )
  }

  if (!course) {
    return <div>Live event not found.</div>
  }

  return (
    <>
      <Breadcrumb title={course.title} />

      <div className="content">
        <div className="container">
          {/* profile box */}
          <ProfileCard />
          {/* profile box */}
          <div className="row">
            {/* sidebar */}
            <StudentSidebar />
            {/* sidebar */}
            <div className="col-lg-9">
              <div className="card bg-light mb-4">
                <div className="card-body d-lg-flex align-items-center">
                  <div className="position-relative">
                    <ImageWithBasePath
                      className="img-fluid rounded-2"
                      src={course.image || '/assets/img/course/course-48.jpg'}
                      fallbackSrc="/assets/img/course/course-48.jpg"
                      alt={course.title}
                    />
                  </div>
                  <div className="w-100 ps-lg-4">
                    <h3 className="mb-2">{course.title}</h3>
                    <p className="fs-14 mb-3">{course.short_description}</p>
                    <div className="d-flex align-items-center gap-2 gap-sm-3 gap-xl-4 flex-wrap my-3 my-sm-0">
                      <p className="fw-medium d-flex align-items-center mb-0">
                        {course.start_date && course.end_date
                          ? `${formatDateWithTimeZone(
                              course.start_date,
                            )} - ${formatDateWithTimeZone(course.end_date)}`
                          : 'Date not specified'}
                      </p>
                      <span className="badge badge-sm rounded-pill bg-warning fs-12">
                        {course.category?.name}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Event Status and Action Buttons */}
              <div className="card mb-4">
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-8">
                      <h5 className="mb-3">Event Status</h5>
                      {eventStatus === 'upcoming' && (
                        <div className="alert alert-info">
                          <strong>Upcoming Event</strong>
                          <p className="mb-0 mt-2">
                            Event will start in:{' '}
                            {countdown.days > 0 && <>{countdown.days} hari </>}
                            {countdown.hours.toString().padStart(2, '0')} :{' '}
                            {countdown.minutes.toString().padStart(2, '0')} :{' '}
                            {countdown.seconds.toString().padStart(2, '0')}
                          </p>
                        </div>
                      )}
                      {eventStatus === 'live' && (
                        <div className="alert alert-success">
                          <strong>Live Now!</strong>
                          <p className="mb-0 mt-2">
                            The event is currently live. Join the class now!
                          </p>
                        </div>
                      )}
                      {eventStatus === 'ended' && (
                        <div className="alert alert-secondary">
                          <strong>Event Ended</strong>
                          <p className="mb-0 mt-2">
                            This event has ended. You can access the recording
                            and download materials.
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="col-md-4">
                      <h5 className="mb-3">Actions</h5>
                      <div className="d-grid gap-2">
                        {attemptInfo.maxAttempt > 0 && (
                          <div className="alert alert-info py-2 mb-0">
                            <i className="fas fa-hourglass-half me-2" />
                            Sisa percobaan kuis: {remainingAttempt} dari{' '}
                            {attemptInfo.maxAttempt}
                          </div>
                        )}
                        {course.link_group && (
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={handleJoinGroup}
                          >
                            <i className="fas fa-users me-2"></i>
                            Join Group
                          </button>
                        )}

                        {course?.is_answered && (
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() =>
                              navigate(
                                all_routes.studentCBTList.replace(
                                  ':id',
                                  String(course.id),
                                ),
                              )
                            }
                          >
                            <i className="fas fa-eye me-2"></i>
                            Detail Skor
                          </button>
                        )}

                        {Number(course?.questions_count || course?.questions?.length || 0) > 0 &&
                          !course?.is_finished &&
                          remainingAttempt > 0 && (
                          <button
                            className="btn btn-info btn-sm"
                            onClick={handleStartQuiz}
                          >
                            <i className="fas fa-play-circle me-2"></i>
                            Mulai Kuis
                          </button>
                        )}

                        {eventStatus === 'live' && (
                          <button
                            className="btn btn-success btn-sm"
                            onClick={handleJoinKelas}
                          >
                            <i className="fas fa-video me-2"></i>
                            Join Kelas
                          </button>
                        )}

                        {eventStatus === 'ended' && course?.link_record && (
                          <button
                            className="btn btn-info btn-sm"
                            onClick={handleAksesRekaman}
                          >
                            <i className="fas fa-play-circle me-2"></i>
                            Akses Rekaman
                          </button>
                        )}

                        <button
                          className="btn btn-warning btn-sm"
                          onClick={handleDownloadSertifikat}
                        >
                          <i className="fas fa-certificate me-2"></i>
                          Download Sertifikat
                        </button>

                        {course.link_ebook && (
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={handleDownloadMateri}
                          >
                            <i className="fas fa-download me-2"></i>
                            Download Materi
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Course Details */}
              <div className="card mb-4">
                <div className="card-body">
                  <h5 className="mb-3">Course Details</h5>
                  <div className="row">
                    <div className="col-md-6">
                      <p>
                        <strong>Category:</strong> {course.category?.name}
                      </p>
                      <p>
                        <strong>Level:</strong> {course.course_level}
                      </p>
                      <p>
                        <strong>Students:</strong> {course.student_count}
                      </p>
                    </div>
                    <div className="col-md-6">
                      <p>
                        <strong>Max Students:</strong> {course.max_student}
                      </p>
                      <p>
                        <strong>Price:</strong>
                        {course.discount ? (
                          <span>
                            {calculateDiscount(
                              parseFloat(course.price),
                              course.discount,
                              course.discount_type as 'PERCENTAGE' | 'NOMINAL',
                            )}
                            <span className="text-decoration-line-through ms-2">
                              {rupiahFormatter(parseFloat(course.price))}
                            </span>
                          </span>
                        ) : (
                          rupiahFormatter(parseFloat(course.price))
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Course Description */}
              <div className="card">
                <div className="card-body">
                  <h5 className="mb-3">Description</h5>
                  <div
                    dangerouslySetInnerHTML={{ __html: course.description }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Certificate Download Modal */}
      {showCertificateModal && (
        <div
          className="modal fade show"
          style={{ display: 'block' }}
          tabIndex={-1}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Download Sertifikat</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowCertificateModal(false)
                    // Reset certificate name when modal is closed
                    setCertificateName('')
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Masukkan nama lengkap Anda yang akan ditampilkan pada
                  sertifikat. Nama ini akan digunakan untuk generate sertifikat
                  secara otomatis.
                </p>
                <div className="mb-3">
                  <label className="form-label">Nama Lengkap</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Masukkan nama lengkap Anda (contoh: John Doe)"
                    value={certificateName}
                    onChange={(e) => setCertificateName(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="alert alert-warning">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  Pastikan nama sudah benar, karena e-sertifikat hanya dapat di
                  generate satu kali.
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCertificateModal(false)
                    // Reset certificate name when modal is closed
                    setCertificateName('')
                  }}
                >
                  Batal
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleConfirmDownload}
                  disabled={!certificateName.trim()}
                >
                  Download Sertifikat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Backdrop */}
      {showCertificateModal && <div className="modal-backdrop fade show"></div>}
    </>
  )
}

export default LiveEventDetails
